/**
 * Assignment Progress Matrix Tab Component - User Timeline Version
 * Shows single user's course completion status across assignment courses
 */

import { Box, Typography } from '@mui/material'
import { AssignmentIcon, CorrectIcon, IncorrectIcon } from 'src/v3/core/icons'
import type { SingleUserAssignmentDetails } from 'src/v3/manage/manage.slice'

interface ProgressMatrixTabProps {
  assignment: SingleUserAssignmentDetails
}

export default function ProgressMatrixTab({
  assignment,
}: ProgressMatrixTabProps) {
  return (
    <Box>
      <Typography variant="h6" className="mb-4 flex items-center gap-2">
        <AssignmentIcon />
        Course Completion Status
      </Typography>
      <Box className="flex gap-4 mb-6 justify-center">
        <Box className="flex items-center gap-1">
          <CorrectIcon sx={{ color: 'success.main', fontSize: 16 }} />
          <Typography variant="caption">Completed</Typography>
        </Box>
        <Box className="flex items-center gap-1">
          <IncorrectIcon sx={{ color: 'error.main', fontSize: 16 }} />
          <Typography variant="caption">Not Completed</Typography>
        </Box>
      </Box>
      <Box
        role="table"
        aria-label="Course completion status"
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
        className="rounded"
      >
        <Box
          role="row"
          sx={{
            bgcolor: 'background.default',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
          className="flex"
        >
          <Box
            role="columnheader"
            sx={{
              flex: 1,
              p: 1.5,
              borderRight: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" className="font-semibold">
              Course
            </Typography>
          </Box>
          <Box role="columnheader" className="min-w-[120px] p-3 text-center">
            <Typography variant="subtitle2" className="font-semibold">
              Status
            </Typography>
          </Box>
        </Box>

        {assignment.courses && assignment.courses.length > 0 ? (
          assignment.courses.map((course, index) => {
            let isCompleted = course.courseCompletionStatus === 'Completed'

            return (
              <Box
                key={course.id || index}
                role="row"
                sx={{
                  borderBottom:
                    index < assignment.courses.length - 1
                      ? '1px solid'
                      : 'none',
                  borderColor: 'divider',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
                className="flex"
              >
                <Box
                  role="cell"
                  sx={{
                    flex: 1,
                    p: 1.5,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" className="font-medium">
                    {course.displayName || course.name || `Course ${index + 1}`}
                  </Typography>
                  {course.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {course.length} CE Hours
                    </Typography>
                  )}
                </Box>
                <Box
                  role="cell"
                  className="min-w-[120px] p-3 text-center flex items-center justify-center"
                >
                  {isCompleted ? (
                    <CorrectIcon
                      sx={{ color: 'success.main', fontSize: 20 }}
                      aria-label="Completed"
                    />
                  ) : (
                    <IncorrectIcon
                      sx={{ color: 'error.main', fontSize: 20 }}
                      aria-label="Not completed"
                    />
                  )}
                </Box>
              </Box>
            )
          })
        ) : (
          <Box className="p-6 text-center">
            <Typography variant="body2" color="text.secondary">
              No courses in this assignment
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          p: 2,
          bgcolor: 'background.paper',
        }}
        className="mt-6 rounded text-center"
      >
        <Typography
          color="warning.main"
          variant="h4"
          sx={{
            fontWeight: 700,
          }}
        >
          {Math.round(assignment.assignmentCompletionPercentage * 100)}%
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Overall Progress
        </Typography>
      </Box>
    </Box>
  )
}
